/**
 * sheetsClient.js
 * ----------------
 * This is the ONLY file that should know how data is actually fetched/stored.
 * Right now it returns mock data shaped exactly like what you'd get back from
 * either the Google Sheets API (https://developers.google.com/sheets/api) or
 * a Google Apps Script web app endpoint that reads/writes a Sheet.
 *
 * HOW TO CONNECT THIS TO REAL GOOGLE SHEETS:
 * Option A (recommended for a teacher-facing app): Deploy a Google Apps Script
 *   as a Web App (Extensions > Apps Script > Deploy > New deployment > Web app).
 *   Have it expose doGet/doPost functions that read/write named ranges or sheet
 *   tabs, returning JSON. Then replace the body of each function below with a
 *   fetch() call to that Web App URL, e.g.:
 *     const res = await fetch(`${APPS_SCRIPT_URL}?action=getDancers&schoolId=${schoolId}`);
 *     return await res.json();
 * Option B: Use the Google Sheets API directly with a service account or
 *   OAuth token, calling spreadsheets.values.get / .update. More setup, more
 *   flexible, but you'd need a small backend or serverless function to hide
 *   credentials (the Sheets API key shouldn't live in frontend code).
 *
 * Every function below is async and returns plain JS objects/arrays so that
 * swapping the implementation never requires touching component code.
 */

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const TEACHER_LOGIN_PATH = `${process.env.PUBLIC_URL || ''}/teacher/login`;

// Every teacher-authenticated call goes through here. If the session has
// expired (401), redirect to the login page instead of letting the caller
// proceed with null data — without this, an expired session shows up as a
// cryptic "Cannot read properties of null" crash on whichever page happened
// to be open.
async function teacherFetch(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, { ...options, credentials: 'include' });
  if (res.status === 401 && window.location.pathname !== TEACHER_LOGIN_PATH) {
    window.location.href = TEACHER_LOGIN_PATH;
  }
  return res;
}

// Collapses genuinely-concurrent calls to the same GET onto one in-flight
// request — nothing is retained once it resolves. TeacherDataContext fires
// every entity's fetcher in the same tick on mount (and, under React
// StrictMode's dev double-invoke of that effect, fires all of them a second
// time immediately after), so without this a single page load could ask the
// backend for e.g. dancers/entries/staff twice each. It's deliberately not a
// stale-serving cache: TeacherDataContext also exposes refetch.<key>() for
// callers that need a guaranteed-fresh read after a write (see
// EventRegistration.js's post-submit refetch), and those must always reach
// the network rather than replay an old response.
function dedupe(fetchFn) {
  let pending = null;
  return (...args) => {
    if (pending) return pending;
    pending = fetchFn(...args).finally(() => { pending = null; });
    return pending;
  };
}

// Unlike dedupe(), this retains its result for the life of the page — safe
// only for content with no mutator and no freshness expectation. The Level/
// Size/Style reference docs (getFieldDefinition) are exactly that: static
// per-field help text that a teacher can reopen the "?" popup for repeatedly
// within one session.
function memoizeForever(fetchFn) {
  const cache = new Map();
  return (key) => {
    if (!cache.has(key)) cache.set(key, fetchFn(key));
    return cache.get(key);
  };
}

const fetchSchool = dedupe(async () => {
  const res = await teacherFetch('/api/teacher/school');
  if (!res.ok) throw new Error(`Failed to load school data (${res.status})`);
  return res.json();
});

const fetchEvent = dedupe(async () => {
  const res = await teacherFetch('/api/teacher/event');
  if (!res.ok) throw new Error(`Failed to load event data (${res.status})`);
  return res.json();
});

const fetchTimeline = dedupe(async () => {
  const res = await teacherFetch('/api/teacher/timeline');
  if (!res.ok) throw new Error(`Failed to load timeline (${res.status})`);
  return res.json();
});

const fetchEntries = dedupe(async () => {
  const res = await teacherFetch('/api/teacher/entries');
  if (!res.ok) throw new Error(`Failed to load entries (${res.status})`);
  return res.json();
});

const fetchDancers = dedupe(async () => {
  const res = await teacherFetch('/api/teacher/dancers');
  if (!res.ok) throw new Error(`Failed to load dancers (${res.status})`);
  return res.json();
});

const fetchGroups = dedupe(async () => {
  const res = await teacherFetch('/api/teacher/groups');
  if (!res.ok) throw new Error(`Failed to load groups (${res.status})`);
  return res.json();
});

const fetchChoreographerLink = dedupe(async () => {
  const res = await teacherFetch('/api/teacher/choreographer-link');
  if (!res.ok) {
    // Surface the backend's message (e.g. "column not found — add a column
    // containing..."), not just a generic status code — this failure mode
    // is a one-time sheet setup step, not a transient network error, so the
    // real reason matters here more than for the other read-only fetchers.
    const body = await res.json().catch(() => ({}));
    throw new Error(body.error || `Failed to load choreographer link (${res.status})`);
  }
  return res.json();
});

const fetchStaff = dedupe(async () => {
  const res = await teacherFetch('/api/teacher/staff');
  if (!res.ok) throw new Error(`Failed to load staff (${res.status})`);
  return res.json();
});

const fetchSchoolLocks = dedupe(async () => {
  const res = await teacherFetch('/api/teacher/school-locks');
  if (!res.ok) throw new Error(`Failed to load lock status (${res.status})`);
  return res.json();
});

const fetchEntryFeePayments = dedupe(async () => {
  const res = await teacherFetch('/api/teacher/entry-fee-payments');
  if (!res.ok) throw new Error(`Failed to load entry fee payments (${res.status})`);
  return res.json();
});

// Shared by getFeeConfig and getInvoiceSummary, which both need the raw
// Prices tab — deduped so a page load that fires both (TeacherDataContext
// mounts every entity's fetcher in the same tick) hits the backend once, not
// twice.
const fetchPrices = dedupe(async () => {
  const res = await teacherFetch('/api/teacher/prices');
  if (!res.ok) throw new Error(`Failed to load prices (${res.status})`);
  return res.json();
});

const fetchRegistrationStatus = dedupe(async () => {
  const res = await teacherFetch('/api/teacher/school-registration-status');
  if (!res.ok) throw new Error(`Failed to load registration status (${res.status})`);
  return res.json();
});

const fetchTimelineDates = dedupe(async () => {
  const res = await teacherFetch('/api/teacher/timeline-dates');
  if (!res.ok) throw new Error(`Failed to load timeline dates (${res.status})`);
  return res.json();
});

const fetchFieldDefinition = memoizeForever(async (field) => {
  const res = await teacherFetch(`/api/teacher/definitions/${field}`);
  if (!res.ok) throw new Error(`Failed to load definitions (${res.status})`);
  return res.json();
});

export const sheetsClient = {
  // ---- SCHOOL / SESSION ----
  getCurrentSchool() {
    return fetchSchool();
  },

  async updateSchool(data) {
    const res = await teacherFetch('/api/teacher/school', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Update failed (${res.status})`);
    }
    return res.json();
  },

  async submitEventRegistration({ estAttendance, estEntries, paymentMethod, acknowledged, firstTimeDancefest, needsNewPlaque, school }) {
    const res = await teacherFetch('/api/teacher/event-registration', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ estAttendance, estEntries, paymentMethod, acknowledged, firstTimeDancefest, needsNewPlaque }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Submission failed (${res.status})`);
    }
    return res.json();
  },

  // ---- DASHBOARD ----
  getEventDetails() {
    return fetchEvent();
  },

  getTimeline() {
    return fetchTimeline();
  },

  // ---- ENTRY LIBRARY ----
  getEntries() {
    return fetchEntries();
  },

  async createEntry(entry) {
    console.log('[sheetsClient] createEntry called with', entry);
    const res = await teacherFetch('/api/register/entry', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(entry),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Update failed (${res.status})`);
    }
    return res.json();
  },

  async saveEntry(entry) {
    const res = await teacherFetch(`/api/teacher/entries/${encodeURIComponent(entry.originalTitle)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(entry),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Update failed (${res.status})`);
    }
    return res.json();
  },

  // Sanitized HTML contents of the Level/Size/Style reference docs, for the
  // entry form's "?" help popups. field is 'level' | 'size' | 'style'.
  // Static reference content — cached for the page's lifetime so reopening
  // the same popup doesn't re-fetch it (see memoizeForever above).
  getFieldDefinition(field) {
    return fetchFieldDefinition(field);
  },

  async deleteEntry(entryId) {
    const res = await teacherFetch(`/api/teacher/entries/${encodeURIComponent(entryId)}`, {
      method: 'DELETE',
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Delete failed (${res.status})`);
    }
    return res.json();
  },

  // ---- ENTRY MUSIC (Cloudflare R2) ----
  // Returns a presigned upload URL — the caller PUTs the file directly to R2,
  // then calls confirmMusicUpload to record it against the entry.
  async getMusicUploadUrl(entryTitle, contentType) {
    const res = await teacherFetch(`/api/teacher/entries/${encodeURIComponent(entryTitle)}/music-upload-url`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ contentType }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Failed to prepare upload (${res.status})`);
    }
    return res.json();
  },

  async confirmMusicUpload(entryTitle, key) {
    const res = await teacherFetch(`/api/teacher/entries/${encodeURIComponent(entryTitle)}/music`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ key }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Failed to save music (${res.status})`);
    }
    return res.json();
  },

  async deleteMusic(entryTitle) {
    const res = await teacherFetch(`/api/teacher/entries/${encodeURIComponent(entryTitle)}/music`, {
      method: 'DELETE',
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Failed to delete music (${res.status})`);
    }
    return res.json();
  },

  async getMusicPreviewUrl(entryTitle) {
    const res = await teacherFetch(`/api/teacher/entries/${encodeURIComponent(entryTitle)}/music-url`);
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Failed to get playback URL (${res.status})`);
    }
    return res.json();
  },

  // ---- DANCERS ----
  // get the dancer information from the backend (which reads from Google Sheets)
  getDancers() {
    return fetchDancers();
  },

  //save updated dancer data to the backend
  async saveDancer(dancer) {
    const res = await teacherFetch('/api/teacher/dancers', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(dancer),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Update failed (${res.status})`);
    }
    return res.json();
  },

  // Removes a dancer from the roster entirely — see the backend route's
  // comment for why this deliberately doesn't touch entries or fee payments.
  async deleteDancer(dancerId) {
    const res = await teacherFetch(`/api/teacher/dancers/${encodeURIComponent(dancerId)}`, {
      method: 'DELETE',
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Delete failed (${res.status})`);
    }
    return res.json();
  },

  // Transfers dancerId's fees/entries to newParticipantId instead of just
  // deleting them — transferEntryTitles is the subset of dancerId's current
  // entries the new dancer is taking over; any not listed just drop dancerId
  // and turn whatever they'd paid into a school credit. See the backend
  // route's comment for the full transfer/credit rules.
  async replaceDancer(dancerId, newParticipantId, transferEntryTitles) {
    const res = await teacherFetch(`/api/teacher/dancers/${encodeURIComponent(dancerId)}/replace`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ newParticipantId, transferEntryTitles }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Replace failed (${res.status})`);
    }
    return res.json();
  },

  //update the parent/guardian email for a dancer in the backend (which updates Google Sheets)
  async updateParentEmail(dancerId, parentEmail) {
    const res = await teacherFetch('/api/teacher/dancers/parent-email', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ participantId: dancerId, parentGuardianEmail: parentEmail }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Update failed (${res.status})`);
    }
    return res.json();
  },

  // Resend the parent email for media release
  async resendParentEmail(dancerId) {
    const res = await teacherFetch('/api/teacher/dancers/resend-consent', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ participantId: dancerId }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Resend failed (${res.status})`);
    }
    return res.json();
  },

  // Bulk-adds students from a teacher-uploaded CSV. students: [{ firstName, lastName, email }]
  async bulkImportDancers(students) {
    const res = await teacherFetch('/api/teacher/dancers/bulk-import', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ students }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Import failed (${res.status})`);
    }
    return res.json();
  },

  // Sends whichever reminder is appropriate to each dancer (registration
  // completion link, or a resent parent consent link). Omit participantIds
  // to run over the whole roster.
  async sendReminders(participantIds) {
    const res = await teacherFetch('/api/teacher/dancers/send-reminders', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(participantIds ? { participantIds } : {}),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Failed to send reminders (${res.status})`);
    }
    return res.json();
  },

  getGroups() {
    return fetchGroups();
  },

  // ---- CHOREOGRAPHER SUBMISSION LINK ----
  getChoreographerLink() {
    return fetchChoreographerLink();
  },

  async regenerateChoreographerLink() {
    const res = await teacherFetch('/api/teacher/choreographer-link/regenerate', { method: 'POST' });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Regenerate failed (${res.status})`);
    }
    return res.json();
  },

  async createGroup({ name, dancerIds }) {
    const res = await teacherFetch('/api/teacher/groups', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, dancerIds }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Create failed (${res.status})`);
    }
    return res.json();
  },

  async saveGroup({ id, name, dancerIds }) {
    const res = await teacherFetch(`/api/teacher/groups/${encodeURIComponent(id)}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, dancerIds }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Update failed (${res.status})`);
    }
    return res.json();
  },

  async deleteGroup(groupId) {
    const res = await teacherFetch(`/api/teacher/groups/${encodeURIComponent(groupId)}`, {
      method: 'DELETE',
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Delete failed (${res.status})`);
    }
    return res.json();
  },

  // ---- TEACHERS / CHAPERONES ----
  getStaff() {
    return fetchStaff();
  },

  // Toggles from the Roster page — the registering teacher decides which
  // staff/chaperones receive DanceFest emails and which get added to the
  // event WhatsApp group, and who supervises workshops. Any field can be
  // omitted to leave it untouched.
  async updateStaffPreferences(staffId, { receiveEmails, addToWhatsapp, workshopSupervisor } = {}) {
    const body = {};
    if (receiveEmails !== undefined) body.receiveEmails = receiveEmails;
    if (addToWhatsapp !== undefined) body.addToWhatsapp = addToWhatsapp;
    if (workshopSupervisor !== undefined) body.workshopSupervisor = workshopSupervisor;
    const res = await teacherFetch(`/api/teacher/staff/${encodeURIComponent(staffId)}/preferences`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      const errBody = await res.json().catch(() => ({}));
      throw new Error(errBody.error || `Update failed (${res.status})`);
    }
    return res.json();
  },

  // ---- INVOICES ----
  async getInvoiceSummary() {
    const [prices, { paid }, dates] = await Promise.all([
      fetchPrices(),
      fetchRegistrationStatus(),
      fetchTimelineDates(),
    ]);

    return {
      schoolRegistrationFee: { amount: prices['School Registration Fee'] || 0, paid },
      schoolRegistrationDueDate: dates['Event Registration'] || '',
      participationFeeDueDate: dates['Participation Fee'] || '',
      // Dancer sign-up (incl. parent consent) closes at the Participant
      // Registration deadline, which is a separate Timeline row from the
      // Participation Fee deadline — see Dashboard.js's isAnyDueSoonOrPastDue.
      participantRegistrationDueDate: dates['Participant Registration'] || '',
      entryFeeDueDate: dates['Dance Entry Payment'] || '',
      musicDueDate: dates['Music Deadline'] || '',
    };
  },
  // Opens the given invoice type's PDF ('school' | 'participants' | 'entries')
  // in a new tab. A plain top-level navigation (not fetch) so the session
  // cookie rides along even though the backend is a different origin in dev
  // — same as clicking a same-site link.
  openInvoicePdf(type) {
    window.open(`${API_BASE}/api/teacher/invoices/${type}.pdf`, '_blank');
  },

  // Whether new participants / new entries are currently locked for this school.
  getSchoolLocks() {
    return fetchSchoolLocks();
  },

  // This school's Entry Fee payments (lump and by-participant) plus any
  // tracked credit — used by Dancers.js's per-dancer payment breakdown. See
  // shared/utils/feePaymentLedger.js for how these get aggregated.
  getEntryFeePayments() {
    return fetchEntryFeePayments();
  },
  // Locks new participant registrations for the school (public form starts
  // rejecting new signups); existing dancers can still be edited.
  async lockParticipants() {
    const res = await teacherFetch('/api/teacher/lock-participants', { method: 'POST' });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Failed to lock participants (${res.status})`);
    }
    return res.json();
  },
  // Locks new entries for the school; existing entries can still be edited
  // (including swapping dancers) but not grown past their current headcount.
  async lockEntries() {
    const res = await teacherFetch('/api/teacher/lock-entries', { method: 'POST' });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Failed to lock entries (${res.status})`);
    }
    return res.json();
  },

  // This school's Most Inspiring Dancer nomination (or null), plus the
  // deadline and whether it has passed (locked).
  async getInspiringDancer() {
    const res = await teacherFetch('/api/teacher/inspiring-dancer');
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Failed to load nomination (${res.status})`);
    }
    return res.json();
  },

  // Creates or updates this school's nomination — { teacherName, participantId, reason }.
  async saveInspiringDancer(nomination) {
    const res = await teacherFetch('/api/teacher/inspiring-dancer', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(nomination),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Failed to save nomination (${res.status})`);
    }
    return res.json();
  },

  // Workshops for this school: { released, eventName, sessions, deadline, locked,
  //   runs: [{ runId, session, title, style, description, spaceName, leaderName,
  //   leaderBio, leaderPhotoUrl, spots, taken, spotsLeft }],
  //   dancers: [{ id, firstName, lastName, session1, session2 }] } (runs/dancers once released).
  async getWorkshops() {
    const res = await teacherFetch('/api/teacher/workshops');
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Failed to load workshops (${res.status})`);
    }
    return res.json();
  },

  // Saves workshop choices — [{ participantId, session1, session2 }], only the dancers that changed.
  // Returns { changed, view } — view is the updated page (same shape as getWorkshops).
  async saveWorkshopAssignments(assignments) {
    const res = await teacherFetch('/api/teacher/workshops', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ assignments }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error || `Failed to save workshop choices (${res.status})`);
    }
    return res.json();
  },

  // Sets the Accommodations & Meals status for the logged-in school —
  // status is '' | 'Request Complete' | 'Payment Complete' | 'Opted Out'.
  async setAccommodationStatus(status) {
    const res = await teacherFetch('/api/teacher/accommodation-status', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      // Full details are logged server-side (see backend console) — keep the
      // teacher-facing message short and non-technical.
      if (body.details) console.error('setAccommodationStatus failed:', body.details);
      throw new Error(body.error || `Failed to update status (${res.status})`);
    }
    return res.json();
  },

  // Sets the Staff & Chaperones Registration status for the logged-in school —
  // status is '' (incomplete) or 'Complete'.
  async setStaffRegistrationStatus(status) {
    const res = await teacherFetch('/api/teacher/staff-registration-status', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      if (body.details) console.error('setStaffRegistrationStatus failed:', body.details);
      throw new Error(body.error || `Failed to update status (${res.status})`);
    }
    return res.json();
  },

  // ---- CONSTANTS ----
  // Entry rates, the participation fee, and the school registration fee all
  // come from the Information sheet's Prices tab (see backend /api/teacher/prices).
  async getFeeConfig() {
    const prices = await fetchPrices();
    return { ENTRY_RATES: prices, PARTICIPATION_FEE: prices['Participation Fee'] || 0 };
  },
};
